import {FC, useCallback} from "react";
import {Box, BoxProps, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {CommentInput} from "./CommentInput";
import {CommentThread} from "./CommentThread";
import {useSearchCommentsInfinite, useCreateComment} from "@/app2/shared/data/repos/comments";

const PAGE_SIZE = 10;

const useStyles = makeStyles()((theme) => ({
	loadMore: {
		display: "flex",
		gap: "10px",
		cursor: "pointer",
		"&:hover": {
			color: theme.palette.action.hover
		}
	}
}));

export interface CommentsTranslations {
	loadMore?: string;
	commentInputPlaceholder?: string;
	replyToUser?: string;
	replies?: string;
	today?: string;
	yesterday?: string;
	daysAgo?: string;
}

export interface CommentsSectionProps extends BoxProps {
	lessonEnrollmentId?: number | null;
	translations?: CommentsTranslations;
}

export const CommentsSection: FC<CommentsSectionProps> = ({
	lessonEnrollmentId,
	translations,
	...baseProps
}) => {
	const {classes} = useStyles();

	const {data, refetch, fetchNextPage, hasNextPage, isFetchingNextPage} = useSearchCommentsInfinite({
		lessonEnrollmentId: lessonEnrollmentId ?? 0,
		pageSize: PAGE_SIZE
	});

	const {mutate: createComment} = useCreateComment();

	const getStringTranslation = useCallback(
		(key: keyof CommentsTranslations, fallback: string): string => {
			const t = translations?.[key];
			return typeof t === "string" ? t : fallback;
		},
		[translations]
	);

	const handlePostComment = useCallback(
		(content: string, threadOf?: number) => {
			if (!lessonEnrollmentId) return;
			createComment(
				{
					content,
					lessonEnrollmentId,
					...(threadOf != null && {threadOf})
				},
				{
					onSuccess: () => refetch()
				}
			);
		},
		[lessonEnrollmentId, createComment, refetch]
	);

	const comments = data?.pages?.flatMap((p) => p.data) ?? [];
	const hasMore = !!hasNextPage;

	const handleLoadMore = useCallback(() => {
		if (hasMore) fetchNextPage();
	}, [hasMore, fetchNextPage]);

	return (
		<Box {...baseProps}>
			<CommentInput
				postComment={handlePostComment}
				placeholder={getStringTranslation("commentInputPlaceholder", "Leave a comment")}
			/>
			{comments.map((comment) =>
				lessonEnrollmentId ? (
					<CommentThread
						key={comment.id}
						comment={comment}
						lessonEnrollmentId={lessonEnrollmentId}
						translations={translations}
					/>
				) : null
			)}
			{hasMore && (
				<Box className={classes.loadMore} onClick={handleLoadMore}>
					<ExpandMoreIcon />
					<Typography>{getStringTranslation("loadMore", "Load more")}</Typography>
					{isFetchingNextPage && <Typography variant="caption">...</Typography>}
				</Box>
			)}
		</Box>
	);
};
